// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — sending a meeting's links (`docs/SHARED_AR_PLAN.md`): the
 * invitation's wording, and the ways it can leave this page — the
 * device's mail app, Gmail and Outlook on the web, WhatsApp, the system
 * share sheet where there is one, and the clipboard. (The meeting panel
 * adds a QR code beside each link.)
 *
 * The web mail clients are opened on their own compose pages with the
 * subject and body filled in; nothing is sent from here, and no address
 * is asked for. Two invitations exist: the audience's, which anyone may
 * forward, and the moderator's, which carries a seat and says so.
 *
 * Lives in `src/ui/` so `check:i18n-strings` covers the wording.
 */

import { t } from '../i18n'

/** Who an invitation is for. */
export type InviteKind = 'audience' | 'moderator'

export interface Invitation {
  subject: string
  body: string
}

/**
 * AYNI: a room code as people read it aloud and type it: in groups of
 * three (an open room's six characters) or five (a meeting's ten).
 */
export function formatRoomCode(code: string): string {
  const size = code.length <= 6 ? 3 : 5
  return code.match(new RegExp(`.{1,${size}}`, 'g'))?.join(' ') ?? code
}

/** The site a link opens, for "go to … and type the code". */
function siteOf(link: string): string {
  try {
    return new URL(link).host
  } catch {
    return ''
  }
}

/** The time a meeting ends, as the reader's clock would show it. */
function endTime(expiresAt: number): string {
  return new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** The invitation for one link. `expiresAt` is null when this page does not know the meeting's end. */
export function invitation(kind: InviteKind, link: string, code: string, expiresAt: number | null): Invitation {
  const params = { code, link, time: expiresAt === null ? '' : endTime(expiresAt), site: siteOf(link), typed: formatRoomCode(code) }
  if (kind === 'moderator') {
    return {
      subject: t('meeting.invite.moderator.subject', params),
      body: t(expiresAt === null ? 'meeting.invite.moderator.bodyOpen' : 'meeting.invite.moderator.body', params),
    }
  }
  return {
    subject: t('meeting.invite.audience.subject', params),
    body: t(expiresAt === null ? 'meeting.invite.audience.bodyOpen' : 'meeting.invite.audience.body', params),
  }
}

/** The device's own mail app. */
export function mailtoUrl(invite: Invitation): string {
  return `mailto:?subject=${encodeURIComponent(invite.subject)}&body=${encodeURIComponent(invite.body)}`
}

/** Gmail's compose page, filled in. */
export function gmailUrl(invite: Invitation): string {
  const q = new URLSearchParams({ view: 'cm', fs: '1', su: invite.subject, body: invite.body })
  return `https://mail.google.com/mail/?${q.toString()}`
}

/** Outlook on the web's compose page, filled in (personal and work accounts both land there). */
export function outlookUrl(invite: Invitation): string {
  const q = new URLSearchParams({ subject: invite.subject, body: invite.body })
  return `https://outlook.office.com/mail/deeplink/compose?${q.toString()}`
}

/**
 * WhatsApp's own share link, with the invitation as the message: it opens
 * the app on a phone, or WhatsApp Web on a computer, to choose who gets it.
 */
export function whatsappUrl(invite: Invitation): string {
  return `https://wa.me/?text=${encodeURIComponent(invite.body)}`
}

/**
 * The row of ways to send one link: mail app, Gmail, Outlook, WhatsApp,
 * the share sheet (only where the browser has one) and copy. `onDone`
 * hears what happened, for the panel's status line.
 */
export function shareButtons(
  invite: Invitation,
  link: string,
  onDone: (message: string) => void,
): HTMLElement {
  const row = document.createElement('div')
  row.className = 'meeting-share-buttons'

  const add = (label: string, onClick: () => void): HTMLButtonElement => {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = label
    button.addEventListener('click', onClick)
    row.appendChild(button)
    return button
  }
  const open = (url: string): void => {
    window.open(url, '_blank', 'noopener')
  }

  add(t('meeting.share.email'), () => {
    window.location.href = mailtoUrl(invite)
  })
  add(t('meeting.share.gmail'), () => open(gmailUrl(invite)))
  add(t('meeting.share.outlook'), () => open(outlookUrl(invite)))
  add(t('meeting.share.whatsapp'), () => open(whatsappUrl(invite)))
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    add(t('meeting.share.more'), () => {
      navigator.share({ title: invite.subject, text: invite.body }).catch(() => {
        // Dismissed: nothing to say.
      })
    })
  }
  const copy = add(t('meeting.share.copy'), () => {
    const done = (): void => {
      copy.textContent = t('meeting.panel.copied')
      onDone(t('meeting.panel.copied'))
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done, () => onDone(link))
    } else {
      onDone(link)
    }
  })
  return row
}
